import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { LIST_MEDIA } from '~/services/training';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface MediaItem {
  _id: string;
  name: string;
  url: string;
  mimetype: string;
  size: number;
  description: string;
}

interface MediaLibraryProps {
  agentId: string;
  onSelectMedia: (media: MediaItem) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const MediaLibrary = ({
  agentId,
  onSelectMedia,
  isOpen,
  onClose,
}: MediaLibraryProps) => {
  const [t] = useTranslation('media_library');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreItems, setHasMoreItems] = useState(true);

  const [fetchMedia, { loading }] = useLazyQuery(LIST_MEDIA, {
    onCompleted: (data) => {
      const response = data.response;

      if (response && !response.hasError) {
        // Map the API response to our MediaItem interface
        const mappedItems = response.data.results.map((item: any) => ({
          _id: item._id,
          name: item.name,
          url: item.url,
          mimetype:
            item.mimetype ||
            (item.type === 'IMAGE' ? 'image/jpeg' : 'video/mp4'),
          size: item.size || 0,
          description: item.description || item.alt_description || '',
        }));

        if (nextCursor) {
          setMediaItems((prev) => [...prev, ...mappedItems]);
        } else {
          setMediaItems(mappedItems);
        }
        setNextCursor(response.data.next_cursor);
        setHasMoreItems(
          response.data.results.length > 0 && response.data.next_cursor,
        );
      }
    },
  });

  useEffect(() => {
    if (isOpen && agentId) {
      fetchMedia({
        variables: {
          data: {
            agent_id: agentId,
            next_cursor: nextCursor,
            limit: 12,
          },
        },
      });
    }
  }, [isOpen, agentId, nextCursor]);

  const loadMore = () => {
    if (nextCursor && hasMoreItems && !loading) {
      fetchMedia({
        variables: {
          data: {
            agent_id: agentId,
            next_cursor: nextCursor,
            limit: 12,
          },
        },
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="max-w-4xl flex max-h-[80vh] max-h-[90vh] w-full flex-col rounded-lg bg-white shadow-lg md:max-w-[70vw]">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-xl font-semibold">{t('media_library')}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading && mediaItems.length === 0 ? (
            <div className="flex h-40 items-center justify-center">
              <div className="loader"></div>
            </div>
          ) : mediaItems.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6">
              {mediaItems.map((item) => (
                <div
                  key={item._id}
                  className="cursor-pointer overflow-hidden rounded-md border transition-shadow hover:shadow-md"
                  onClick={() => {
                    onSelectMedia(item);
                    onClose();
                  }}
                >
                  <div className="flex aspect-square items-center justify-center bg-gray-100">
                    {item.mimetype.startsWith('image') ? (
                      <img
                        src={`${import.meta.env.VITE_API_URL}/media/${
                          item.url
                        }`}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : item.mimetype.startsWith('video') ? (
                      <video
                        src={`${import.meta.env.VITE_API_URL}/media/${
                          item.url
                        }`}
                        className="h-full w-full object-cover"
                        controls
                      />
                    ) : (
                      <DocumentMagnifyingGlassIcon className="h-12 w-12 text-gray-400" />
                    )}
                  </div>
                  <div className="p-2">
                    <p className="truncate text-sm" title={item.name}>
                      {item.name}
                    </p>
                    <p
                      className="truncate text-xs text-gray-500"
                      title={item.description}
                    >
                      {item.description || t('no_description')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center text-gray-500">
              <DocumentMagnifyingGlassIcon className="h-12 w-12" />
              <p>{t('no_media_found')}</p>
            </div>
          )}
        </div>

        {hasMoreItems && (
          <div className="flex justify-center border-t p-4">
            <button
              onClick={loadMore}
              disabled={loading}
              className="rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? t('loading') : t('load_more')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaLibrary;
